import AddIcon from "@mui/icons-material/Add";
import CancelIcon from "@mui/icons-material/Cancel";
import {
  Box,
  Button,
  Chip,
  FormControl,
  FormLabel,
  IconButton,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import React, { useState } from "react";
import ProfessionalAffiliation from "../../../../types/ProfessionalAffiliation";
import { gray } from "../../../shared-theme/themePrimitives";
import { ResponsibilitiesInput } from "./components/ResponsibilitiesInput";
import { TechnologiesAutocomplete } from "./components/TechnologiesAutocomplete";
import { AffiliationDateRangePicker } from "./components/AffilationDateRangePicker";
import Position from "../../../../types/Position";
import Skill from "../../../../types/Skill";
import ConfirmationDialog from "../../../shared/CustomConfirmationDialog";
import { sortProjectsByDate } from "../../../utils/helpers";

const inputHeight = {
  "& .MuiOutlinedInput-root": {
    height: "50px",
    boxSizing: "border-box",
  },
};

type ConfirmState = {
  open: boolean;
  title: string;
  message: string;
  onConfirm: () => void;
};

interface ExperienceSectionProps {
  index: number;
  affiliation: ProfessionalAffiliation;
  handleInput: (e: any, index: number, projectIndex?: number) => void;
  addProject: (index: number) => void;
  removeProject: (affiliationIndex: number, projectIndex: number) => void;
  removeAffiliation: (affiliationIndex: number) => void;
  errors: Record<string, string>;
  removeErrorMessage: (field: string) => void;
  positions: Position[];
  skills: Skill[];
}

export const ExperienceSection: React.FC<ExperienceSectionProps> = ({
  handleInput,
  index,
  affiliation,
  addProject,
  removeProject,
  removeAffiliation,
  errors,
  removeErrorMessage,
  positions,
  skills,
}) => {
  const [hoveredProject, setHoveredProject] = useState<number | null>(null);
  const [hoveredAffiliation, setHoveredAffiliation] = useState(false);
  const [confirm, setConfirm] = useState<ConfirmState>({
    open: false,
    title: "",
    message: "",
    onConfirm: () => {},
  });

  const openConfirm = (opts: Omit<ConfirmState, "open">) =>
    setConfirm({ open: true, ...opts });

  const closeConfirm = () => setConfirm((s) => ({ ...s, open: false }));

  return (
    <Box id={`affiliation-${index}`}>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "1rem",
          marginY: "1rem",
        }}
      >
        <Box
          id={`affiliation-${index}-header`}
          sx={{
            display: "flex",
            flexDirection: "column",
            width: "100%",
            gap: 2,
            border: `1px solid ${gray[200]}`,
            borderRadius: "8px",
            padding: "1rem",
            marginBottom: "1rem",
            position: "relative",
            "&:hover": {
              border: `1px solid ${gray[400]}`,
            },
          }}
          onMouseEnter={() => setHoveredAffiliation(true)}
          onMouseLeave={() => setHoveredAffiliation(false)}
        >
          {/* Affiliation remove button */}
          {hoveredAffiliation && (
            <IconButton
              aria-label="Remove affiliation"
              title="Remove affiliation"
              onClick={() =>
                openConfirm({
                  title: "Are you sure that you want to remove affiliation?",
                  message: `This will delete "${
                    affiliation.employer || "this employer"
                  }" and all of its projects.`,
                  onConfirm: () => {
                    removeAffiliation(index);
                    closeConfirm();
                  },
                })
              }
              sx={{
                position: "absolute",
                top: -10,
                right: -10,
                border: "none",
                backgroundColor: gray[100],
                zIndex: 1,
                "&:hover": { backgroundColor: gray[100] },
              }}
            >
              <CancelIcon />
            </IconButton>
          )}
          <Stack flexDirection="row" justifyContent="space-between">
            <FormControl>
              <FormLabel htmlFor={`employer-${index}`}>Employer</FormLabel>
              <TextField
                id={`employer-${index}`}
                name="employer"
                value={affiliation.employer || ""}
                required
                placeholder="Employer"
                error={!!errors[`affiliations[${index}].employer`]}
                helperText={errors[`affiliations[${index}].employer`]}
                onChange={(e) => {
                  removeErrorMessage(`affiliations[${index}].employer`);
                  handleInput(e, index);
                }}
                fullWidth
                variant="outlined"
                color="primary"
                sx={inputHeight}
              />
            </FormControl>
            <FormControl>
              <FormLabel htmlFor={`location-${index}`}>Location</FormLabel>
              <TextField
                id={`location-${index}`}
                name="location"
                value={affiliation.location || ""}
                required
                placeholder="Location"
                error={!!errors[`affiliations[${index}].location`]}
                helperText={errors[`affiliations[${index}].location`]}
                onChange={(e) => {
                  removeErrorMessage(`affiliations[${index}].location`);
                  handleInput(e, index);
                }}
                fullWidth
                variant="outlined"
                color="primary"
                sx={inputHeight}
              />
            </FormControl>
            <FormControl>
              <FormLabel htmlFor={`country-${index}`}>Country</FormLabel>
              <TextField
                id={`country-${index}`}
                name="country"
                value={affiliation.country || ""}
                required
                placeholder="Country"
                error={!!errors[`affiliations[${index}].country`]}
                helperText={errors[`affiliations[${index}].country`]}
                onChange={(e) => {
                  removeErrorMessage(`affiliations[${index}].country`);
                  handleInput(e, index);
                }}
                fullWidth
                variant="outlined"
                color="primary"
                sx={inputHeight}
              />
            </FormControl>
          </Stack>
          <AffiliationDateRangePicker
            affiliation={affiliation}
            index={index}
            handleInput={handleInput}
            label="Date Period"
            value="clientPeriod"
            errors={errors}
            removeErrorMessage={removeErrorMessage}
          />
        </Box>
      </Box>
      {sortProjectsByDate(affiliation.projects).map((project) => {
        const projectIndex = affiliation.projects.indexOf(project);
        return (
        <Box
          key={projectIndex}
          id={`affiliation-${index}-project-${projectIndex}`}
          sx={{
            display: "flex",
            flexDirection: "column",
            width: "100%",
            gap: 2,
            border: `1px solid ${gray[200]}`,
            borderRadius: "8px",
            padding: "1rem",
            marginBottom: "1rem",
            position: "relative",
            "&:hover": {
              border: `1px solid ${gray[400]}`,
            },
          }}
          onMouseEnter={() => setHoveredProject(projectIndex)}
          onMouseLeave={() => setHoveredProject(null)}
        >
          {hoveredProject === projectIndex && (
            <IconButton
              onClick={() =>
                openConfirm({
                  title: "Are you sure that you want to remove project?",
                  message: `This will delete project ${projectIndex + 1} for "${
                    affiliation.employer || "this employer"
                  }".`,
                  onConfirm: () => {
                    removeProject(index, projectIndex);
                    closeConfirm();
                  },
                })
              }
              sx={{
                position: "absolute",
                top: -10,
                right: -10,
                border: "none",
                backgroundColor: gray[100],
                "&:hover": {
                  backgroundColor: gray[100],
                },
              }}
            >
              <CancelIcon />
            </IconButton>
          )}
          <FormControl fullWidth>
            <FormLabel htmlFor={`position-${index}-${projectIndex}`}>
              Positions
            </FormLabel>
            <Select
              id={`position-${index}-${projectIndex}`}
              name="position"
              multiple
              value={project.position || []}
              onChange={(e) => {
                removeErrorMessage(
                  `affiliations[${index}].projects[${projectIndex}].position`
                );
                handleInput(e, index, projectIndex);
              }}
              displayEmpty
              renderValue={(selected) => {
                const sel = (selected as string[]).filter(Boolean);
                if (sel.length === 0) {
                  return (
                    <span style={{ opacity: 0.6 }}>Select a position</span>
                  );
                }
                return (
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                    {sel.map((value, chipIndex) => (
                      <Chip
                        key={`${value}-${chipIndex}`}
                        label={value}
                        size="small"
                        onDelete={(e) => {
                          e.stopPropagation();
                          e.preventDefault();
                          const newPosition = sel.filter(
                            (_, i) => i !== chipIndex
                          );
                          handleInput(
                            {
                              target: { name: "position", value: newPosition },
                            },
                            index,
                            projectIndex
                          );
                          removeErrorMessage(
                            `affiliations[${index}].projects[${projectIndex}].position`
                          );
                        }}
                        onClick={(e) => e.stopPropagation()}
                        onMouseDown={(e) => e.stopPropagation()}
                      />
                    ))}
                  </Box>
                );
              }}
              variant="outlined"
              color="primary"
              error={
                !!errors[
                  `affiliations[${index}].projects[${projectIndex}].position`
                ]
              }
              sx={{
                minHeight: "50px",
                background: "none",
                "& .MuiSelect-select": {
                  display: "flex",
                  alignItems: "center",
                  minHeight: "32px",
                },
              }}
              MenuProps={{
                PaperProps: {
                  sx: () => ({
                    "&& .MuiButtonBase-root": {
                      margin: "2px",
                    },
                    // selected state
                    "&& .MuiButtonBase-root.Mui-selected": {
                      backgroundColor: gray[200],
                    },
                  }),
                },
              }}
            >
              {positions.map((pos) => (
                <MenuItem key={pos.name} value={pos.name}>
                  {pos.name}
                </MenuItem>
              ))}
              <MenuItem value="Internship">Internship</MenuItem>
            </Select>
            {errors[
              `affiliations[${index}].projects[${projectIndex}].position`
            ] && (
              <Typography fontSize={"12px"} color="error" marginTop={"3px"}>
                {
                  errors[
                    `affiliations[${index}].projects[${projectIndex}].position`
                  ]
                }
              </Typography>
            )}
          </FormControl>
          {!project.position.includes("Internship") && (
            <>
              <FormControl>
                <FormLabel htmlFor={`client-${index}-${projectIndex}`}>
                  Client/Project
                </FormLabel>
                <TextField
                  id={`client-${index}-${projectIndex}`}
                  name="client"
                  error={
                    !!errors[
                      `affiliations[${index}].projects[${projectIndex}].client`
                    ]
                  }
                  helperText={
                    errors[
                      `affiliations[${index}].projects[${projectIndex}].client`
                    ]
                  }
                  required
                  placeholder="Client/Project"
                  value={project.client || ""}
                  onChange={(e) => {
                    handleInput(e, index, projectIndex);
                    removeErrorMessage(
                      `affiliations[${index}].projects[${projectIndex}].client`
                    );
                  }}
                  fullWidth
                  variant="outlined"
                  color="primary"
                  sx={inputHeight}
                />
              </FormControl>
              <FormControl>
                <FormLabel htmlFor={`role-${index}-${projectIndex}`}>
                  Role
                </FormLabel>
                <TextField
                  id={`role-${index}-${projectIndex}`}
                  name="role"
                  error={
                    !!errors[
                      `affiliations[${index}].projects[${projectIndex}].role`
                    ]
                  }
                  helperText={
                    errors[
                      `affiliations[${index}].projects[${projectIndex}].role`
                    ]
                  }
                  required
                  placeholder="Role"
                  value={project.role || ""}
                  onChange={(e) => {
                    handleInput(e, index, projectIndex);
                    removeErrorMessage(
                      `affiliations[${index}].projects[${projectIndex}].role`
                    );
                  }}
                  fullWidth
                  variant="outlined"
                  color="primary"
                  sx={inputHeight}
                />
              </FormControl>
              <AffiliationDateRangePicker
                affiliation={affiliation}
                index={index}
                handleInput={handleInput}
                label="Project Period"
                value="projectPeriod"
                projectIndex={projectIndex}
                errors={errors}
                removeErrorMessage={removeErrorMessage}
              />
              <FormControl>
                <FormLabel
                  htmlFor={`projectDescription-${index}-${projectIndex}`}
                >
                  Project Description
                </FormLabel>
                <TextField
                  id={`projectDescription-${index}-${projectIndex}`}
                  name="projectDescription"
                  error={
                    !!errors[
                      `affiliations[${index}].projects[${projectIndex}].projectDescription`
                    ]
                  }
                  helperText={
                    errors[
                      `affiliations[${index}].projects[${projectIndex}].projectDescription`
                    ]
                  }
                  required
                  placeholder="Project Description"
                  value={project.projectDescription || ""}
                  onChange={(e) => {
                    handleInput(e, index, projectIndex);
                    removeErrorMessage(
                      `affiliations[${index}].projects[${projectIndex}].projectDescription`
                    );
                  }}
                  fullWidth
                  multiline
                  minRows={4}
                  variant="outlined"
                  color="primary"
                />
              </FormControl>
            </>
          )}
          <ResponsibilitiesInput
            affiliation={affiliation}
            index={index}
            projectIndex={projectIndex}
            handleInput={handleInput}
            errors={errors}
            removeErrorMessage={removeErrorMessage}
          />

          <FormControl>
            <FormLabel htmlFor={`keyTechnologies-${index}-${projectIndex}`}>
              Key Technologies
            </FormLabel>
            <TechnologiesAutocomplete
              affiliation={affiliation}
              index={index}
              projectIndex={projectIndex}
              handleInput={handleInput}
              errors={errors}
              removeErrorMessage={removeErrorMessage}
              skills={skills}
            />
          </FormControl>
        </Box>
        );
      })}

      {/* Button to Add a New Project for the Same Employer */}
      <Button
        variant="contained"
        startIcon={<AddIcon />}
        onClick={() => addProject(index)}
        fullWidth
        sx={{ marginBottom: "1rem" }}
      >
        Add Another Project
      </Button>

      {/* Confirmation dialog */}
      <ConfirmationDialog
        open={confirm.open}
        onClose={closeConfirm}
        onConfirm={confirm.onConfirm}
        title={confirm.title}
        message={confirm.message}
        confirmColor="error"
        confirmLabel="Delete"
        cancelLabel="Cancel"
      />
    </Box>
  );
};
